/**
 * Admin → Settings (thresholds do tenant).
 *
 * Define os defaults globais usados pelo engine de alertas em todos os
 * equipments do tenant: bateria, sinal, offline, hysteresis, renotify.
 *
 * Regras específicas (alert_rules) podem sobrescrever via params.
 *
 * Acesso:
 *   - admin do tenant: edita o próprio tenant (sem seletor).
 *   - super_admin: precisa escolher o tenant via seletor no topo.
 */

import { useEffect, useMemo, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AlertCircle, Building2, Map, Save, Send } from "lucide-react";
import toast from "react-hot-toast";

import { tenantSettingsApi } from "@/api/alerts";
import { tenantsApi } from "@/api/tenants";
import { apiErrorMessage } from "@/api/client";
import { alertQk, useNotificationChannels, useTenantAlertSettings, useTenants } from "@/hooks/queries";
import { useAuth } from "@/contexts/AuthContext";
import type { TenantAlertSettings, TenantAlertSettingsUpdate } from "@/types";

import { PageHeader } from "@/components/PageHeader";
import { Card, CardBody, CardHeader } from "@/components/Card";
import { Field, Input, Select } from "@/components/Field";
import { Tip } from "@/components/Tip";
import { Button } from "@/components/Button";
import { Loading } from "@/components/Loading";
import { EmptyState } from "@/components/EmptyState";

export function AdminSettingsPage() {
  const auth = useAuth();
  const tenants = useTenants(auth.isSuperAdmin);
  const qc = useQueryClient();

  // Super_admin escolhe; admin do tenant não vê o select e usa o próprio.
  const [pickedTenantId, setPickedTenantId] = useState<string>("");
  const effectiveTenantId = auth.isSuperAdmin ? pickedTenantId : undefined;
  const canFetch = !auth.isSuperAdmin || !!pickedTenantId;

  const settings = useTenantAlertSettings(effectiveTenantId, canFetch);

  // Canais pra dropdown do briefing semanal — filtra só os tipos suportados.
  // Só habilita quando temos tenant definido, senão dispara com params vazios.
  const channelsQuery = useNotificationChannels(effectiveTenantId, canFetch);
  const briefingChannelOptions = (channelsQuery.data ?? [])
    .filter((c) => c.kind === "email" || c.kind === "whatsapp")
    .map((c) => ({ value: c.id, label: `${c.name} (${c.kind})` }));

  // -- Form derivado, não setState -------------------------------------------
  // Antes usávamos useState + useEffect(setForm(settings.data)). O problema:
  // se settings.data nunca chegar (erro, race, timeout), form ficava null
  // e a UI travava em <Loading /> pra sempre.
  //
  // Agora: form é DERIVADO da query + edições locais (dirty). Assim, form
  // reflete sempre o estado atual da query — sem risco de dessincronizar.
  const [dirty, setDirty] = useState<Partial<TenantAlertSettings>>({});
  const form = useMemo<TenantAlertSettings | null>(() => {
    if (!settings.data) return null;
    return { ...settings.data, ...dirty };
  }, [settings.data, dirty]);

  // Reset das edições locais quando troca de tenant (superadmin) ou quando
  // a query traz dados novos e não temos edição pendente.
  useEffect(() => {
    setDirty({});
  }, [pickedTenantId]);

  const updateMut = useMutation({
    mutationFn: (payload: TenantAlertSettingsUpdate) =>
      tenantSettingsApi.update(payload, effectiveTenantId),
    onSuccess: () => {
      toast.success("Thresholds atualizados");
      setDirty({});  // limpa edições locais — passou a valer o novo data
      // Invalida prefix-only pra cobrir variações de tenantId.
      void qc.invalidateQueries({ queryKey: ["tenant-alert-settings"] });
    },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });

  const briefingMut = useMutation({
    mutationFn: () => tenantSettingsApi.enviarBriefingAgora(effectiveTenantId),
    onSuccess: () => {
      // 202, não 200: o pedido entrou na fila. Dizer "enviado" aqui seria
      // afirmar algo que ainda não aconteceu — e se falhar no analytics, o
      // usuário teria ouvido "enviado" e nada teria chegado.
      toast.success("Briefing na fila — sai em até um minuto");
      void qc.invalidateQueries({ queryKey: ["tenant-alert-settings"] });
    },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!form) return;
    const { tenant_id: _tid, updated_at: _ua, ...payload } = form;
    updateMut.mutate(payload);
  }

  function patchForm(patch: Partial<TenantAlertSettings>) {
    setDirty((prev) => ({ ...prev, ...patch }));
  }

  function num(field: keyof TenantAlertSettings) {
    return {
      type: "number",
      value: String(form?.[field] ?? ""),
      onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
        const v = e.target.value === "" ? 0 : parseInt(e.target.value, 10);
        patchForm({ [field]: v } as Partial<TenantAlertSettings>);
      },
    };
  }

  // ---- Render ----

  const showTenantPicker = auth.isSuperAdmin;
  const needsPickerSelection = auth.isSuperAdmin && !pickedTenantId;

  return (
    <div className="mx-auto max-w-3xl">
      <PageHeader
        title="Padrões do cliente"
        description="Valores herdados por todos os ativos. Cada ativo pode sobrescrever o que precisar em Thresholds, e o que ela não sobrescrever continua vindo daqui."
      />

      {/* Seletor de tenant pra super_admin */}
      {showTenantPicker && (
        <Card className="mb-6">
          <CardBody>
            <Field
              label={
                <span className="flex items-center gap-2">
                  <Building2 className="h-4 w-4 text-brand-600" />
                  Tenant
                </span>
              }
            >
              <Select
                value={pickedTenantId}
                onChange={(e) => setPickedTenantId(e.target.value)}
                options={(tenants.data ?? []).map((t) => ({
                  value: t.id,
                  label: `${t.name} (${t.slug})`,
                }))}
                placeholder="Selecione um tenant..."
              />
            </Field>
          </CardBody>
        </Card>
      )}

      {needsPickerSelection ? (
        <EmptyState
          title="Selecione um tenant"
          description="As configurações de threshold são por cliente. Escolha qual deles você quer editar acima."
        />
      ) : settings.isError ? (
        <EmptyState
          icon={<AlertCircle className="h-8 w-8 text-red-500" />}
          title="Não foi possível carregar os thresholds"
          description={apiErrorMessage(settings.error)}
          action={
            <Button variant="secondary" onClick={() => settings.refetch()}>
              Tentar novamente
            </Button>
          }
        />
      ) : settings.isLoading || !form ? (
        <Loading />
      ) : (
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Recursos opcionais do cliente.
              Fica no topo e fora do formulário de thresholds porque salva
              sozinho, noutra rota (PATCH do tenant) — misturar com o Salvar
              lá de baixo faria um botão prometer gravar duas coisas e gravar
              uma. */}
          {auth.isSuperAdmin && effectiveTenantId && (
            <RecursosDoCliente tenantId={effectiveTenantId} />
          )}

          {/* Bateria */}
          <Card>
            <CardHeader title="Bateria" subtitle="Quando avisar e quando alertar como crítico." />
            <CardBody>
              <div className="grid gap-4 sm:grid-cols-2">
                <Field
                  label={
                    <>
                      Aviso (warning) %
                      <Tip label="Quando avisar por bateria">
                        Abaixo deste nível o sensor entra em observação, sem
                        urgência. Padrão de fábrica: 20%.
                      </Tip>
                    </>
                  }
                >
                  <Input {...num("battery_warning_pct")} min={0} max={100} />
                </Field>
                <Field
                  label={
                    <>
                      Crítico %
                      <Tip label="Quando a bateria é crítica">
                        Troca de pilha urgente — abaixo disso o sensor pode
                        parar a qualquer momento. Padrão de fábrica: 10%.
                      </Tip>
                    </>
                  }
                >
                  <Input {...num("battery_critical_pct")} min={0} max={100} />
                </Field>
              </div>
            </CardBody>
          </Card>

          {/* Sinal */}
          <Card>
            <CardHeader title="Sinal" subtitle="Threshold de qualidade de rede — separado por tipo." />
            <CardBody>
              <div className="grid gap-4 sm:grid-cols-2">
                <Field
                  label={
                    <>
                      WiFi · aviso (dBm)
                      <Tip label="Como ler a escala dBm">
                        Escala negativa: -60 é bom, -90 é ruim. Quanto mais
                        negativo, pior. Padrão de fábrica: -80.
                      </Tip>
                    </>
                  }
                >
                  <Input {...num("signal_warning_dbm")} max={0} min={-120} />
                </Field>
                <Field label="WiFi · crítico (dBm)">
                  <Input {...num("signal_critical_dbm")} max={0} min={-120} />
                </Field>
                <Field
                  label={
                    <>
                      Zigbee · aviso (LQI)
                      <Tip label="O que é LQI">
                        Link Quality Indicator, de 0 a 255 — ao contrário do
                        dBm, aqui maior é melhor. Padrão de fábrica: 50.
                      </Tip>
                    </>
                  }
                >
                  <Input {...num("signal_warning_lqi")} min={0} max={255} />
                </Field>
                <Field label="Zigbee · crítico (LQI)">
                  <Input {...num("signal_critical_lqi")} min={0} max={255} />
                </Field>
              </div>
            </CardBody>
          </Card>

          {/* Offline + portas */}
          <Card>
            <CardHeader title="Tempos" subtitle="Janelas em minutos pra disparar alertas temporais." />
            <CardBody>
              <div className="grid gap-4 sm:grid-cols-2">
                <Field
                  label={
                    <>
                      Offline AC (min)
                      <Tip label="Como o sensor AC reporta">
                        Tomada e probe Wi-Fi têm energia de sobra e reportam a
                        cada poucos segundos. Medido em campo: mediana de 6s,
                        pior intervalo de 6 min. Padrão de fábrica: 15 min.
                      </Tip>
                    </>
                  }
                >
                  <Input {...num("offline_after_minutes")} min={1} max={10080} />
                </Field>
                <Field
                  label={
                    <>
                      Offline bateria (min)
                      <Tip label="Por que bateria tem limite próprio">
                        Sensor Zigbee dorme entre leituras pra durar meses —
                        reporta a cada 30-60 min, e o de porta pode ficar horas
                        calado sem que haja problema. Padrão de fábrica: 120 min.
                      </Tip>
                    </>
                  }
                >
                  <Input {...num("battery_offline_after_minutes")} min={1} max={10080} />
                </Field>
                <Field
                  label={
                    <>
                      Porta aberta (min)
                      <Tip label="Como conta o tempo de porta aberta">
                        Tempo <strong>contínuo</strong> de porta aberta. Fechar
                        e abrir de novo reinicia a contagem. Padrão de fábrica:
                        5 min.
                      </Tip>
                    </>
                  }
                >
                  <Input {...num("door_open_warning_minutes")} min={1} max={1440} />
                </Field>
              </div>
            </CardBody>
          </Card>

          {/* Anti-flapping */}
          <Card>
            <CardHeader
              title="Anti-flapping"
              subtitle="Evita avalanche de alertas em sensor oscilando."
            />
            <CardBody>
              <div className="grid gap-4 sm:grid-cols-2">
                <Field
                  label={
                    <>
                      Confirmar após N leituras
                      <Tip label="O que é confirmar após N leituras">
                        Quantas avaliações seguidas precisam concordar antes do
                        alerta mudar de estado. Protege contra ruído de medição.
                        Evento binário — porta, offline, relé — não usa: é
                        inequívoco já na primeira leitura. Padrão: 3.
                      </Tip>
                    </>
                  }
                >
                  <Input {...num("hysteresis_count")} min={1} max={20} />
                </Field>
                <Field
                  label={
                    <>
                      Re-notificar a cada (min)
                      <Tip label="Quando a notificação se repete">
                        Enquanto o alerta continuar ativo e ninguém tiver
                        resolvido, a notificação se repete nesse intervalo.
                        Padrão: 60 min.
                      </Tip>
                    </>
                  }
                >
                  <Input {...num("renotify_minutes")} min={1} max={10080} />
                </Field>
              </div>
            </CardBody>
          </Card>

          <Card>
            <CardHeader
              title="Chamada telefônica"
              subtitle="Vale para os canais do tipo Chamada telefônica deste cliente."
            />
            <CardBody>
              <div className="grid gap-4 sm:grid-cols-2">
                <Field
                  label={
                    <>
                      Limite de ligações por dia
                      <Tip label="Por que existe um teto">
                        Ligação custa e acorda gente. Um sensor com defeito
                        pode gerar dezenas de alertas numa madrugada, e o teto
                        é o freio. Vale para o cliente inteiro, somando todos
                        os alertas. Zero desliga as ligações sem apagar o canal
                        nem as inscrições — útil durante manutenção.
                      </Tip>
                    </>
                  }
                >
                  <Input {...num("voice_calls_per_day")} min={0} max={100} />
                </Field>
                <Field
                  label={
                    <>
                      Pausa entre rodadas (min)
                      <Tip label="O ritmo da insistência">
                        Depois de ligar para todos os números sem ninguém
                        reconhecer, o sistema espera esse tempo e recomeça.
                        Com 2 números e os padrões (10 ligações, 10 min), a
                        cota rende cerca de uma hora de insistência. Sem a
                        pausa ela queimaria em minutos e o resto do dia
                        ficaria sem ligação nenhuma.
                      </Tip>
                    </>
                  }
                >
                  <Input {...num("voice_round_interval_min")} min={1} max={240} />
                </Field>
              </div>
            </CardBody>
          </Card>

          <Card>
            <CardHeader
              title="Custos"
              subtitle="Tarifa usada no card 'Indicadores do período' dos ativos."
            />
            <CardBody>
              <Field
                label={
                  <>
                    Tarifa de energia (R$/kWh)
                    <Tip label="Para que serve a tarifa">
                      Multiplica o kWh consumido pra estimar custo no card
                      Indicadores do período. Padrão: 0,85 (média ANEEL
                      residencial).
                    </Tip>
                  </>
                }
              >
                <Input
                  type="number"
                  step="0.01"
                  min={0}
                  max={100}
                  value={form.tariff_per_kwh?.toString() ?? ""}
                  onChange={(e) =>
                    patchForm({ tariff_per_kwh: Number(e.target.value) || 0 })
                  }
                />
              </Field>
            </CardBody>
          </Card>

          {/* envpulse AI — briefing semanal */}
          <Card>
            <CardHeader
              title={
                <span className="inline-flex items-center gap-2">
                  <span className="inline-flex h-5 w-5 items-center justify-center rounded-md bg-gradient-to-br from-violet-500 to-cyan-500 text-white">
                    <svg width="11" height="11" viewBox="0 0 24 24" fill="currentColor"><path d="M12 0l2.5 9.5L24 12l-9.5 2.5L12 24l-2.5-9.5L0 12l9.5-2.5z"/></svg>
                  </span>
                  envpulse <span className="font-semibold text-violet-600 dark:text-violet-400">AI</span>
                </span>
              }
              subtitle="Briefing semanal automatizado — toda sexta-feira de manhã."
            />
            <CardBody className="space-y-4">
              <label className="flex items-center gap-3">
                <input
                  type="checkbox"
                  checked={form.weekly_briefing_enabled}
                  onChange={(e) =>
                    patchForm({ weekly_briefing_enabled: e.target.checked })
                  }
                  className="h-4 w-4 rounded border-surface-300 dark:border-slate-700 text-brand-600"
                />
                <div>
                  <div className="text-sm font-medium text-slate-700 dark:text-slate-300">
                    Habilitar briefing semanal
                  </div>
                  <div className="text-xs text-slate-500 dark:text-slate-400">
                    Resumo de saúde de todos os ativos do cliente, com
                    findings e os três mais críticos. Sai na sexta a partir
                    das 7h.
                  </div>
                </div>
              </label>

              {form.weekly_briefing_enabled && (
                <Field
                  label="Canal de entrega"
                  hint={
                    briefingChannelOptions.length === 0
                      ? "Nenhum canal email ou whatsapp configurado. Crie um em /admin/alerts → Canais."
                      : "Email ou WhatsApp configurado em /admin/alerts → Canais."
                  }
                >
                  <Select
                    value={form.weekly_briefing_channel_id ?? ""}
                    onChange={(e) =>
                      patchForm({ weekly_briefing_channel_id: e.target.value || null })
                    }
                    options={[
                      { value: "", label: "— selecionar —" },
                      ...briefingChannelOptions,
                    ]}
                    disabled={briefingChannelOptions.length === 0}
                  />
                </Field>
              )}

              {form.last_briefing_sent_at && (
                <div className="rounded-md bg-surface-50/50 dark:bg-slate-900/50 p-2.5 text-xs text-slate-600 dark:text-slate-400">
                  Último briefing enviado em{" "}
                  <span className="font-medium text-slate-700 dark:text-slate-300">
                    {new Date(form.last_briefing_sent_at).toLocaleString("pt-BR")}
                  </span>
                </div>
              )}

              {/* Esperar até sexta pra descobrir se o canal está certo é o
                  contrário de configurar. */}
              <div className="flex items-center gap-3">
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  icon={<Send className="h-4 w-4" />}
                  loading={briefingMut.isPending}
                  disabled={!form.weekly_briefing_channel_id}
                  onClick={() => briefingMut.mutate()}
                >
                  Enviar agora
                </Button>
                <span className="text-xs text-slate-500 dark:text-slate-400">
                  {form.weekly_briefing_channel_id
                    ? "Sai em até um minuto, sem esperar a sexta."
                    : "Escolha o canal antes."}
                </span>
              </div>
            </CardBody>
          </Card>

          <Card>
            <CardHeader
              title="Internet de reserva do gateway"
              subtitle="Quando o gateway passa a enviar pelo 4G porque a internet do local caiu."
            />
            <CardBody>
              <label className="flex items-center gap-3">
                <input
                  type="checkbox"
                  checked={form.conexao_reserva_avisa_cliente ?? false}
                  onChange={(e) =>
                    patchForm({ conexao_reserva_avisa_cliente: e.target.checked })
                  }
                  className="h-4 w-4 rounded border-surface-300 dark:border-slate-700 text-brand-600"
                />
                <div>
                  <div className="text-sm font-medium text-slate-700 dark:text-slate-300">
                    Avisar também o cliente
                  </div>
                  <div className="text-xs text-slate-500 dark:text-slate-400">
                    Desligado, só a equipe LAIT é avisada. Ligado, o aviso
                    aparece nas Ocorrências e vai pelos canais inscritos.
                  </div>
                </div>
              </label>
            </CardBody>
          </Card>

          <div className="flex justify-end">
            <Button
              type="submit"
              loading={updateMut.isPending}
              icon={<Save className="h-4 w-4" />}
            >
              Salvar thresholds
            </Button>
          </div>
        </form>
      )}
    </div>
  );
}


// ---------------------------------------------------------------------------

/**
 * Recursos opcionais por cliente.
 *
 * Hoje só a planta baixa. Existe porque recurso que não se aplica a um
 * cliente não pode custar a parte mais cara da tela: com o card ligado e sem
 * planta nenhuma, o dashboard abre com um retângulo vazio na primeira dobra.
 *
 * Desligar NÃO APAGA NADA — nem a imagem, nem as posições dos ativos. É o que
 * torna o botão seguro de usar: quem desliga por engano religa e encontra
 * tudo onde estava.
 *
 * Só super_admin: ligar um recurso de produto para um cliente é decisão de
 * quem vende, não de quem opera. A rota (PATCH do tenant) já cobra isso.
 */
function RecursosDoCliente({ tenantId }: { tenantId: string }) {
  const qc = useQueryClient();
  const tenants = useTenants(true);
  const tenant = tenants.data?.find((t) => t.id === tenantId);

  const mut = useMutation({
    mutationFn: (ligado: boolean) =>
      tenantsApi.update(tenantId, { plantas_habilitadas: ligado }),
    onSuccess: (_d, ligado) => {
      toast.success(ligado ? "Planta baixa ligada" : "Planta baixa desligada");
      // Os sites carregam a flag junto (ela vem do tenant), então a lista de
      // unidades precisa recarregar pro dashboard obedecer sem F5.
      void qc.invalidateQueries({ queryKey: ["tenants"] });
      void qc.invalidateQueries({ queryKey: ["sites"] });
    },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });

  if (!tenant) return null;
  const ligado = tenant.plantas_habilitadas ?? false;

  return (
    <Card>
      <CardHeader
        title="Recursos"
        subtitle="O que este cliente enxerga no produto."
      />
      <CardBody>
        <label className="flex items-start gap-3">
          <input
            type="checkbox"
            checked={ligado}
            disabled={mut.isPending}
            onChange={(e) => mut.mutate(e.target.checked)}
            className="mt-0.5 h-4 w-4 rounded border-surface-300 text-brand-600 focus:ring-brand-400 dark:border-slate-600 dark:bg-slate-800"
          />
          <span>
            <span className="flex items-center gap-1.5 text-sm font-medium text-slate-800 dark:text-slate-200">
              <Map className="h-4 w-4 text-brand-600" aria-hidden />
              Planta baixa das unidades
            </span>
            <span className="mt-0.5 block text-xs text-slate-500 dark:text-slate-400">
              Mostra a planta no dashboard, na tela da unidade e no modo
              painel, e libera a seção no relatório. Desligado, o cliente não
              vê o card — as imagens e as posições já cadastradas continuam
              guardadas.
            </span>
          </span>
        </label>
      </CardBody>
    </Card>
  );
}
